import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, Snackbar, TextField, Typography } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { loginWithCredentials } from '../api/authApi';
import { persistSession } from '../../../shared/auth/session';
import { usePermissions } from '../../../shared/auth/PermissionsContext';

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

const REQUEST_ACCOUNT_MAILTO = `mailto:luisaugustos@usal.es?${new URLSearchParams({
  subject: 'Solicitud de acceso al sistema',
  body: 'Hola,\n\nMe gustaría solicitar acceso al sistema. Mis datos son:\n\nNombre y apellidos:\nProfesión:\n',
}).toString().replace(/\+/g, '%20')}`;

export default function LoginForm() {
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [openBad, setOpenBad] = useState(false);
  const [openBaja, setOpenBaja] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Correo o contraseña incorrectos.');
  const [inactiveMessage, setInactiveMessage] = useState('Tu cuenta no está habilitada todavía. Debe ser activada por un administrador.');

  const redirectAfterLogin = (isAdmin) => {
    navigate(isAdmin ? '/homeAdmin' : '/home');
  };

  const handleSuccessfulLogin = (responseData) => {
    persistSession({ token: responseData.token, userId: responseData.id });
    refreshPermissions();
    setOpenGood(true);
    redirectAfterLogin(responseData.is_admin === true);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await loginWithCredentials({ email, password });
      handleSuccessfulLogin(response.data);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        setInactiveMessage(error.response.data?.message || 'Tu cuenta no está habilitada todavía. Debe ser activada por un administrador.');
        setOpenBaja(true);
      } else if (status === 429) {
        setErrorMessage(error.response.data?.message || 'Demasiados intentos. Inténtalo de nuevo en unos minutos.');
        setOpenBad(true);
      } else {
        setErrorMessage('Correo o contraseña incorrectos.');
        setOpenBad(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937' }}>Iniciar sesión</Typography>
      <Typography sx={{ mt: 1, mb: 4, color: '#6b7280' }}>Introduce tus credenciales para acceder a tu cuenta.</Typography>

      <form onSubmit={handleLogin} noValidate>
        <TextField
          label="Correo electrónico"
          type="email"
          required
          fullWidth
          autoFocus
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          sx={{ ...inputSx, mb: 2.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Contraseña"
          type={showPassword ? 'text' : 'password'}
          required
          fullWidth
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          sx={inputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{ mt: 4, py: 1.4, borderRadius: 2, bgcolor: '#0D6AEE', fontWeight: 600 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Iniciar sesión'}
        </Button>
      </form>

      <Typography variant="body2" align="center" sx={{ mt: 3, color: '#6b7280' }}>
        ¿No tienes cuenta?{' '}
        <Link href={REQUEST_ACCOUNT_MAILTO} sx={{ fontWeight: 600 }}>
          Solicítala aquí
        </Link>
      </Typography>

      <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
        <Alert severity="success" sx={{ borderRadius: 2 }}>Inicio de sesión exitoso.</Alert>
      </Snackbar>
      <Snackbar open={openBad} autoHideDuration={3000} onClose={() => setOpenBad(false)}>
        <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setOpenBad(false)}>{errorMessage}</Alert>
      </Snackbar>
      <Snackbar open={openBaja} autoHideDuration={3000} onClose={() => setOpenBaja(false)}>
        <Alert severity="warning" sx={{ borderRadius: 2 }} onClose={() => setOpenBaja(false)}>{inactiveMessage}</Alert>
      </Snackbar>
    </Box>
  );
}
